import { ApiError, RequestAbortError, type CompanionPrefs } from '../../api/client.js';
import { h, toast, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import { isValidServerBase, type SessionController } from '../../session/controller.js';
import { ASIS_MOBILE_SERVER_BASE, getMobileBridge } from '../../platform/mobile-bridge.js';
import type { DesktopTidyController } from '../components/desktop-tidy.js';

export function createAjustesView(controller: SessionController, ctx: { version: string | null; isElectron: boolean; desktopTidy?: DesktopTidyController | null }): View {
  let active = true;
  let currentPrefs: CompanionPrefs | null = null;
  const lifetime = new AbortController();
  const mobile = getMobileBridge();
  const root = h('section', { class: 'view view-ajustes', id: 'view-ajustes' });
  root.appendChild(renderHeader('ajustes'));
  const body = h('div', { class: 'section-body' });
  root.appendChild(body);

  // Server
  const serverCard = h('section', { class: 'card' });
  serverCard.append(h('h2', { class: 'card-title' }, ['Servidor']));
  const form = h('form', { class: 'form-stack' });
  const urlLabel = h('label', { class: 'field' }, []);
  urlLabel.append(h('span', {}, ['Dirección del servidor']));
  const urlInput = h('input', { class: 'input', type: 'url', placeholder: 'https://app.asisdot.com' });
  urlInput.value = controller.getServerBase();
  urlLabel.appendChild(urlInput);
  const actions = h('div', { class: 'form-row' });
  const saveBtn = h('button', { type: 'submit', class: 'btn primary' }, ['Guardar']);
  const logoutBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Cerrar sesión']);
  if (!mobile) actions.append(saveBtn);
  actions.append(logoutBtn);
  form.append(mobile ? h('p', {class:'card-sub'}, [ASIS_MOBILE_SERVER_BASE]) : urlLabel, actions);
  const help = h('p', { class: 'card-sub' }, [mobile ? 'La aplicación móvil conecta con tu cuenta de ASIS en este servidor.' : 'Cambiar de servidor cierra cualquier sesión de voz activa y obliga a autenticarse de nuevo en el nuevo origen.']);
  serverCard.append(form, help);
  const connectionDetails = h('details', { class: 'secondary-details' }, [h('summary', {}, ['Conexión avanzada']), serverCard]);
  body.appendChild(connectionDetails);

  // Companion preferences
  const prefsCard = h('section', { class: 'card companion-prefs' });
  prefsCard.append(
    h('h2', { class: 'card-title' }, ['Rutinas y foco']),
    h('p', { class: 'card-sub' }, ['Elige tu ritmo y las preferencias de tus unidades.']),
  );
  const prefsForm = h('form', { class: 'form-stack' });
  const timezone = h('input', { class: 'input', required: true, maxlength: '64', autocomplete: 'off', 'aria-label': 'Zona horaria', placeholder: 'Europe/Madrid' });
  const focusMinutes = h('input', { class: 'input', type: 'number', min: '1', max: '180', step: '1', required: true, 'aria-label': 'Minutos de foco' });
  const volume = h('input', { class: 'input', type: 'number', min: '0', max: '100', step: '1', required: true, 'aria-label': 'Volumen' });
  const idleAnimations = h('input', { type: 'checkbox' });
  const quietDuringFocus = h('input', { type: 'checkbox' });
  const prefsGrid = h('div', { class: 'companion-prefs-grid' }, [
    h('label', { class: 'field companion-prefs-wide' }, [h('span', {}, ['Zona horaria']), timezone]),
    h('label', { class: 'field' }, [h('span', {}, ['Minutos de foco']), focusMinutes]),
    h('label', { class: 'field' }, [h('span', {}, ['Volumen (0–100)']), volume]),
  ]);
  const prefsStatus = h('p', { class: 'muted companion-prefs-status', role: 'status', 'aria-live': 'polite' }, ['Cargando preferencias…']);
  const prefsSave = h('button', { class: 'btn primary', type: 'submit' }, ['Guardar preferencias']);
  prefsForm.append(
    prefsGrid,
    h('label', { class: 'field inline' }, [idleAnimations, h('span', {}, ['Animaciones en reposo'])]),
    h('label', { class: 'field inline' }, [quietDuringFocus, h('span', {}, ['Silencio durante el foco'])]),
    h('div', { class: 'form-row' }, [prefsSave]),
    prefsStatus,
  );
  prefsCard.appendChild(prefsForm);
  body.prepend(prefsCard);

  // Voice / model
  const voiceCard = h('section', { class: 'card' });
  voiceCard.append(h('h2', { class: 'card-title' }, ['Modelo de voz']));
  const voiceKv = h('dl', { class: 'kv' });
  const providerRow = h('div', { class: 'kv-row' }, [h('dt', {}, ['Proveedor']), h('dd', { id: 'kv-voice-provider' }, ['—'])]);
  const modelRow = h('div', { class: 'kv-row' }, [h('dt', {}, ['Modelo']), h('dd', { id: 'kv-voice-model' }, ['—'])]);
  const rateRow = h('div', { class: 'kv-row' }, [h('dt', {}, ['Frecuencia']), h('dd', { id: 'kv-voice-rate' }, ['—'])]);
  const cfgRow = h('div', { class: 'kv-row' }, [h('dt', {}, ['Estado']), h('dd', { id: 'kv-voice-cfg' }, ['—'])]);
  voiceKv.append(providerRow, modelRow, rateRow, cfgRow);
  voiceCard.appendChild(voiceKv);
  connectionDetails.append(voiceCard);

  // App
  const appCard = h('section', { class: 'card' });
  appCard.append(h('h2', { class: 'card-title' }, ['Aplicación']));
  const appKv = h('dl', { class: 'kv' });
  const verRow = h('div', { class: 'kv-row' }, [h('dt', {}, ['Versión']), h('dd', {}, [ctx.version ?? (mobile ? 'No disponible' : 'Aplicación web')])]);
  const envRow = h('div', { class: 'kv-row' }, [h('dt', {}, ['Entorno']), h('dd', {}, [mobile ? (mobile.platform === 'ios' ? 'Aplicación iOS' : 'Aplicación Android') : ctx.isElectron ? 'Electron (escritorio)' : 'Navegador'])]);
  appKv.append(verRow, envRow);
  appCard.appendChild(appKv);
  if (!ctx.isElectron && !mobile) appCard.append(h('p', { class: 'card-sub' }, ['En iPhone: Compartir → Añadir a pantalla de inicio. En Android: menú del navegador → Instalar aplicación. Inicia sesión con la misma cuenta del Mac para compartir recuerdos, actividad y dispositivos. La voz funciona con la aplicación abierta.']));
  connectionDetails.appendChild(appCard);

  if (ctx.desktopTidy) {
    const tidyCard = h('section', { class: 'card desktop-tidy-settings' });
    tidyCard.append(
      h('h2', { class: 'card-title' }, ['Escritorio']),
      h('p', { class: 'card-sub' }, ['Prepara un plan para ordenar solo los archivos sueltos. Nada se mueve hasta que lo confirmes y podrás deshacerlo.']),
      h('div', { class: 'form-row' }, [ctx.desktopTidy.createButton()]),
    );
    body.appendChild(tidyCard);
  }

  const api = controller.getApi();

  async function loadVoice(): Promise<void> {
    try {
      const v = await api.getVoiceConfig(lifetime.signal, controller.getState().kind === 'local');
      if (!active) return;
      if (!active) return;
      (voiceCard.querySelector('#kv-voice-provider') as HTMLElement).textContent = v.provider;
      (voiceCard.querySelector('#kv-voice-model') as HTMLElement).textContent = v.providerModel;
      (voiceCard.querySelector('#kv-voice-rate') as HTMLElement).textContent = `${v.sampleRate} Hz`;
      (voiceCard.querySelector('#kv-voice-cfg') as HTMLElement).textContent = v.providerConfigured === true ? 'Configurado' : v.providerConfigured === false ? 'Sin configurar' : 'No informado';
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      (voiceCard.querySelector('#kv-voice-provider') as HTMLElement).textContent = '—';
      (voiceCard.querySelector('#kv-voice-model') as HTMLElement).textContent = '—';
      (voiceCard.querySelector('#kv-voice-rate') as HTMLElement).textContent = '—';
      (voiceCard.querySelector('#kv-voice-cfg') as HTMLElement).textContent = 'No disponible';
    }
  }

  function fillPrefs(prefs: CompanionPrefs): void {
    currentPrefs = prefs;
    timezone.value = prefs.timezone;
    focusMinutes.value = String(prefs.focusMinutes);
    volume.value = String(prefs.volume);
    idleAnimations.checked = prefs.idleAnimations;
    quietDuringFocus.checked = prefs.quietDuringFocus;
  }

  async function loadPrefs(): Promise<void> {
    prefsStatus.textContent = 'Cargando preferencias…';
    try {
      const response = await api.getCompanionPrefs(lifetime.signal);
      if (!active) return;
      fillPrefs(response.prefs);
      prefsStatus.textContent = '';
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      prefsStatus.textContent = `No se pudieron cargar las preferencias: ${apiMessage(err)}.`;
    }
  }

  prefsForm.addEventListener('submit', async event => {
    event.preventDefault();
    if (prefsSave.disabled) return;
    const prefs: CompanionPrefs = {
      timezone: timezone.value.trim(),
      focusMinutes: Number(focusMinutes.value),
      breakMinutes: currentPrefs?.breakMinutes ?? 5,
      volume: Number(volume.value),
      idleAnimations: idleAnimations.checked,
      quietDuringFocus: quietDuringFocus.checked,
    };
    prefsSave.disabled = true;
    prefsStatus.textContent = 'Guardando preferencias…';
    try {
      const response = await api.updateCompanionPrefs(prefs, lifetime.signal);
      if (!active) return;
      fillPrefs(response.prefs);
      prefsStatus.textContent = 'Preferencias guardadas.';
      toast('Preferencias guardadas', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      prefsStatus.textContent = `No se pudieron guardar: ${apiMessage(err)}.`;
    } finally { prefsSave.disabled = false; }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (mobile) return;
    const raw = urlInput.value.trim();
    if (!isValidServerBase(raw)) {
      toast('Origen no válido. Usa https:// o http://localhost/127.0.0.1 sin ruta.', 'error');
      return;
    }
    saveBtn.disabled = true;
    try {
      await controller.switchServerBase(raw);
      toast('Servidor cambiado. Vuelve a iniciar sesión.', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      toast(`No se pudo cambiar: ${apiMessage(err)}`, 'error');
    } finally { saveBtn.disabled = false; }
  });

  logoutBtn.addEventListener('click', async () => {
    logoutBtn.disabled = true;
    try { await controller.logout(); } catch { toast('Se ha cerrado la vista local, pero no se ha confirmado el cierre en el servidor. Recupera la conexión y vuelve a cerrar sesión.', 'error'); }
  });

  void loadVoice(); void loadPrefs();
  return { el: root, destroy() { active = false; lifetime.abort(); }, update() { void loadVoice(); void loadPrefs(); } };
}

function apiMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return (err as Error).message ?? 'error';
}
